/**
 * Deals Page with Server-Side Search
 *
 * This version uses in-memory search index for fast pagination, sorting, and filtering.
 * Tree data (products) is disabled in this mode - deals are flat.
 *
 * Toggle between this and DealsPage via feature flag or route.
 */

import { Alert, Box, Button } from '@mui/material'
import {
  GridCellParams,
  GridColDef,
  GridDensity,
  GridFilterModel,
  GridRowClassNameParams,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useDeleteDealItems } from 'api/aws/organizations_table/useDeleteDealItems'
import { useUserItems } from 'api/aws/users/useUserItems'
import { checkDealsSearchHealth, useServerSideDeals } from 'api/search'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { calculateWeightedTotal } from 'components/DataGrid/operators/calculateWeightedTotal'
import { loadModel, saveModel } from 'components/DataGrid/operators/loadModels'
import { DealsGridFooter } from 'components/Deals/grid/DealsGridFooter'
import { gridContainerStyles } from 'components/Deals/style'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import '../deals.css'
import {
  defaultColumnOrder,
  defaultFilterModel,
  defaultSortModel,
  defaultVisibilityModel,
  pinnedColumns,
} from './defaultModels'
import { getDealCellClassNames } from './getDealCellClassNames'
import { getDealRowClassNames } from './getDealRowClassNames'
import { useDealGridColumns } from './useDealGridColumns'

const CONTEXT = 'deals'

const DealsPageServerSide = () => {
  const navigate = useNavigate()
  const { pinned } = useLayoutContext()
  const { showSnackbar } = useSnackbarContext()
  const { data: users } = useUserItems()
  const { dealGridColumns } = useDealGridColumns()
  const apiRef = useGridApiRef()
  const { mutate: deleteItems } = useDeleteDealItems()

  // UI State
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('dealsDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })

  // Server-side search state
  const [searchAvailable, setSearchAvailable] = useState<boolean | null>(null)
  const [syncStatus, setSyncStatus] = useState<{
    documentCount?: number
    lastSyncTime?: string
    isInitialized?: boolean
  }>({})

  // Check if search is available on mount
  useEffect(() => {
    const checkHealth = async () => {
      const result = await checkDealsSearchHealth()
      setSearchAvailable(result.available)
      setSyncStatus({
        documentCount: result.documentCount,
        lastSyncTime: result.lastSyncTime,
        isInitialized: result.isInitialized,
      })
      if (!result.available) {
        console.warn('[DealsPageServerSide] Search not available:', result.message)
      }
    }

    checkHealth()
    const interval = setInterval(checkHealth, 30000)
    return () => clearInterval(interval)
  }, [])

  // Load saved models from localStorage
  const savedSortModel = useMemo(() => loadModel<GridSortModel>(CONTEXT, 'SortModel', defaultSortModel), [])
  const savedFilterModel = useMemo(() => loadModel<GridFilterModel>(CONTEXT, 'FilterModel', defaultFilterModel), [])

  // Use server-side hook
  const {
    rows: serverRows,
    aggregations,
    totalRowCount,
    isLoading: isSearchLoading,
    error: searchError,
    sortModel,
    onSortModelChange: hookSortModelChange,
    filterModel,
    onFilterModelChange: hookFilterModelChange,
    serverSideProps,
    refresh,
  } = useServerSideDeals({
    enabled: searchAvailable === true,
    pageSize: 100,
    initialSortModel: savedSortModel,
    initialFilterModel: savedFilterModel,
  })

  const isLoading = searchAvailable === null || searchAvailable === false || isSearchLoading

  // Persist sort/filter changes to localStorage
  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      saveModel(CONTEXT, 'SortModel', newSortModel)
      hookSortModelChange(newSortModel)
    },
    [hookSortModelChange]
  )

  const onFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      saveModel(CONTEXT, 'FilterModel', newFilterModel)
      hookFilterModelChange(newFilterModel)
    },
    [hookFilterModelChange]
  )

  const onClearFilters = useCallback(() => {
    const emptyFilters = { items: [], quickFilterValues: [] }
    saveModel(CONTEXT, 'FilterModel', emptyFilters)
    hookFilterModelChange(emptyFilters)
  }, [hookFilterModelChange])

  // Build user lookup map
  const userById = useMemo(() => {
    const map = new Map()
    ;(users ?? []).forEach(u => {
      if (u.user_uuid) map.set(u.user_uuid, u)
      if (u.id) map.set(u.id, u)
    })
    return map
  }, [users])

  // Process rows to add client-side computed fields
  const processedRows = useMemo(() => {
    if (!serverRows) return []

    return serverRows.map(deal => {
      // Use server-resolved entities when available, fallback to client-side lookup
      const owner = deal._resolvedOwner || (deal?.owner?.[0] ? userById.get(deal.owner[0]) : undefined)

      const organization = deal._resolvedOrganization || null

      return {
        ...deal,
        rowId: deal.SK || deal.deal_uuid,
        id: deal.deal_uuid || deal.SK,
        organization,
        owner,
        hasChildRows: false, // No tree data in server-side mode
        weightedTotal: calculateWeightedTotal(deal),
      }
    })
  }, [serverRows, userById])

  // Selection handler
  const changeRowSelectionModel = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      if (newSelectionModel?.ids?.size === 1) {
        const deal = processedRows?.find(d => d?.SK === Array.from(newSelectionModel?.ids)?.[0])
        setSelectedDeal(deal)
      }
    },
    [processedRows]
  )

  // Delete handlers
  const handleDeleteClick = useCallback(
    (rowSelectionModel: GridRowSelectionModel) => {
      const selectedRowId = Array.from(rowSelectionModel?.ids)?.[0]
      const rowElement = apiRef.current.getRowElement(selectedRowId)
      setAnchorEl(rowElement)
    },
    [apiRef]
  )

  const handleClosePopover = useCallback(
    (rowSelectionModel: GridRowSelectionModel) => {
      setAnchorEl(null)
      if (Array.from(rowSelectionModel?.ids)?.length > 0) {
        Array.from(rowSelectionModel?.ids).forEach(id => {
          apiRef.current.selectRow(id, false)
        })
        setSelectedDeal(null)
      }
    },
    [apiRef]
  )

  const handleConfirmDelete = useCallback(
    async (rowSelectionModel: GridRowSelectionModel) => {
      // Clear selection first
      try {
        if (apiRef.current?.setRowSelectionModel) {
          apiRef.current.setRowSelectionModel({ ids: new Set<string>(), type: 'include' } as any)
        }
      } catch {
        // intentionally empty
      }

      setIsDeleting(true)

      const dealToDelete = Array.from(rowSelectionModel?.ids ?? [])
        .map(id => processedRows.find(deal => deal.SK === id))
        .filter(Boolean)
        .map(deal =>
          deal?.predecessorId
            ? { PK: deal!.PK, SK: deal!.SK, predecessorId: deal!.predecessorId }
            : { PK: deal!.PK, SK: deal!.SK }
        )

      if (!dealToDelete.length) {
        showSnackbar('No valid deals selected for deletion.', 'error')
        setIsDeleting(false)
        return
      }

      deleteItems(
        { EntityType: 'deal', items: dealToDelete },
        {
          onSuccess: () => {
            setAnchorEl(null)
            setSelectedDeal(null)
            showSnackbar(
              `${dealToDelete.length} deal${dealToDelete.length > 1 ? 's' : ''} deleted successfully`,
              'success'
            )
            setIsDeleting(false)
            refresh() // Refresh search results
          },
          onError: err => {
            console.error('Failed to delete deal(s):', err)
            showSnackbar(err?.message || 'Failed to delete deal(s)', 'error')
            setIsDeleting(false)
          },
        }
      )
    },
    [processedRows, deleteItems, showSnackbar, refresh, apiRef]
  )

  const handleEditClick = useCallback(() => {
    if (selectedDeal) {
      navigate(`/deals/${selectedDeal?.PK}/${selectedDeal?.SK}/agreement`)
    }
  }, [selectedDeal, navigate])

  const changeDensity = useCallback((newDensity: GridDensity) => {
    setDensity(newDensity)
    localStorage.setItem('dealsDensityModel', newDensity)
  }, [])

  // No-op toggleExpanded for server-side mode (no tree)
  const toggleExpanded = useCallback(() => {}, [])
  const isExpanded = useCallback(() => false, [])
  const handleNavigate = (route: string) => navigate(route)
  const columns: GridColDef[] = useMemo(() => dealGridColumns(processedRows, density, handleNavigate), [
    processedRows,
    density,
    navigate,
    dealGridColumns,
  ])

  const defaultColumnWidths = useMemo(
    () => Object.fromEntries(columns.filter(col => typeof col.width === 'number').map(col => [col.field, col.width])),
    [columns]
  )

  const getRowClassNameWrapper = useCallback(
    (params: GridRowClassNameParams, rowSelectionModel: GridRowSelectionModel) =>
      getDealRowClassNames(params, rowSelectionModel, selectedDeal),
    [selectedDeal]
  )

  const getCellClassNameWrapper = useCallback((params: GridCellParams) => getDealCellClassNames(params), [])

  const FooterWrapper = useCallback(
    ({
      rowSelectionModel,
      setRowSelectionModel,
    }: {
      rowSelectionModel: GridRowSelectionModel
      setRowSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>
    }) => (
      <DealsGridFooter
        rowSelectionModel={rowSelectionModel}
        deals={processedRows}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        onClickDelete={handleDeleteClick}
        handleConfirmDelete={handleConfirmDelete}
        handleClosePopover={handleClosePopover}
        handleEditClick={handleEditClick}
        selectedData={selectedDeal}
        apiRef={apiRef}
        serverSideMode={true}
      />
    ),
    [
      processedRows,
      anchorEl,
      handleDeleteClick,
      handleConfirmDelete,
      handleClosePopover,
      handleEditClick,
      selectedDeal,
      apiRef,
    ]
  )

  const gridContainerSx = useMemo(
    () => ({
      position: 'relative',
      ...gridContainerStyles,
      pt: pinned ? '60px' : 0.8,
      pb: 2,
      transition: 'all 0.3s ease-in-out',
      height: '100%',
    }),
    [pinned]
  )

  // Show error if search failed
  if (searchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Search error: {searchError}
        </Alert>
        <Button variant="outlined" onClick={() => refresh()}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <>
      <GlobalDataGrid
        rows={processedRows}
        columns={columns}
        isExpanded={isExpanded}
        apiRef={apiRef}
        context={'deals'}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        pinnedColumns={pinnedColumns}
        onRowSelectionModelChange={changeRowSelectionModel}
        aggregationModel={{
          total: 'sum',
          monthlyTotal: 'sum',
          weightedTotal: 'sum',
          implementationFee: 'sum',
          probability: 'avg',
        }}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={FooterWrapper}
        onChangeDensity={changeDensity}
        getRowClassName={getRowClassNameWrapper}
        getCellClassName={getCellClassNameWrapper}
        // gridContainerSx={gridContainerSx}
        treeData={false}
        isLoading={isLoading}
        // Server-side mode props
        serverSideMode={true}
        rowCount={totalRowCount}
        paginationModel={serverSideProps.paginationModel}
        onPaginationModelChange={serverSideProps.onPaginationModelChange}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onClearFilters={onClearFilters}
        syncStatus={syncStatus}
      />
    </>
  )
}

export default DealsPageServerSide
